import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../components/Layout';
import { apiFetch } from '../../lib/api';
import { useFiltroSocieta } from '../../hooks/useFiltroSocieta';
import ApiInspector from '../../components/ApiInspector';
import { API_DOCS } from './LogStoricoInviiPage.api';

// ── Tipi ──────────────────────────────────────────────────────────────────
interface ReportLog {
  id: number;
  societaId: number;
  templateId: number;
  utenteId: number;
  tipoAzione: string;
  entitaType: string;
  entitaId: number;
  destinatarioEmail: string | null;
  esito: string;
  erroreDettaglio: string | null;
  createdAt: string;
  template: { nome: string; moduloGestionale: string };
  utente: { nome: string | null; cognome: string | null; email: string };
}

interface TemplateOption {
  id: number;
  nome: string;
}

const BADGE_AZIONE: Record<string, string> = {
  PREVIEW: 'bop-badge-neutral',
  PDF: 'bop-badge-info',
  EMAIL: 'bop-badge-warning',
};

const BADGE_ESITO: Record<string, string> = {
  OK: 'bop-badge-success',
  ERRORE: 'bop-badge-danger',
};

export default function LogStoricoInviiPage() {
  const { societaId, pronta } = useFiltroSocieta();

  const [logs, setLogs] = useState<ReportLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [templateOptions, setTemplateOptions] = useState<TemplateOption[]>([]);

  // Filtri
  const [filtroTemplate, setFiltroTemplate] = useState('');
  const [filtroAzione, setFiltroAzione] = useState('');
  const [filtroEsito, setFiltroEsito] = useState('');
  const [filtroDa, setFiltroDa] = useState('');
  const [filtroA, setFiltroA] = useState('');

  // Carica opzioni template
  useEffect(() => {
    if (!pronta || !societaId) return;
    (async () => {
      try {
        // @api GET /api/report-templates?societaId= -> {"success":true,"data":[...]}
        const r = await apiFetch<{ success: boolean; data: TemplateOption[] }>(`/api/report-templates?societaId=${societaId}`);
        setTemplateOptions(r.data || []);
      } catch { /* ignora */ }
    })();
  }, [pronta, societaId]);

  // Carica logs
  const caricaLogs = useCallback(async () => {
    if (!societaId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('societaId', societaId);
      if (filtroTemplate) params.set('templateId', filtroTemplate);
      if (filtroAzione) params.set('tipoAzione', filtroAzione);
      if (filtroEsito) params.set('esito', filtroEsito);
      if (filtroDa) params.set('from', filtroDa);
      if (filtroA) params.set('to', filtroA);

      // @api GET /api/report-logs?societaId=&templateId=&tipoAzione=&esito=&from=&to= -> {"success":true,"data":[...]}
      const r = await apiFetch<{ success: boolean; data: ReportLog[] }>(`/api/report-logs?${params.toString()}`);
      setLogs(r.data || []);
    } catch { /* errore */ } finally {
      setLoading(false);
    }
  }, [societaId, filtroTemplate, filtroAzione, filtroEsito, filtroDa, filtroA]);

  useEffect(() => {
    if (pronta && societaId) caricaLogs();
  }, [pronta, societaId, caricaLogs]);

  if (!pronta) {
    return (
      <Layout>
        <div className="bop-page-header"><h1 className="bop-page-title">Log & Storico Invii</h1></div>
        <div className="bop-card"><div className="bop-empty-state">Seleziona una società</div></div>
        <ApiInspector docs={API_DOCS} />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="bop-page-header">
        <h1 className="bop-page-title">Log & Storico Invii</h1>
      </div>

      <div className="bop-filter-bar">
        <select className="bop-select" value={filtroTemplate} onChange={e => setFiltroTemplate(e.target.value)}>
          <option value="">Tutti i template</option>
          {templateOptions.map(t => <option key={t.id} value={t.id}>{t.nome}</option>)}
        </select>
        <select className="bop-select" value={filtroAzione} onChange={e => setFiltroAzione(e.target.value)}>
          <option value="">Tutte le azioni</option>
          <option value="PREVIEW">PREVIEW</option>
          <option value="PDF">PDF</option>
          <option value="EMAIL">EMAIL</option>
        </select>
        <select className="bop-select" value={filtroEsito} onChange={e => setFiltroEsito(e.target.value)}>
          <option value="">Tutti gli esiti</option>
          <option value="OK">OK</option>
          <option value="ERRORE">ERRORE</option>
        </select>
        <input className="bop-input" type="date" value={filtroDa} onChange={e => setFiltroDa(e.target.value)} placeholder="Da" title="Data da" />
        <input className="bop-input" type="date" value={filtroA} onChange={e => setFiltroA(e.target.value)} placeholder="A" title="Data a" />
      </div>

      <div className="bop-text-muted">Mostrati gli ultimi 200 invii</div>

      <div className="bop-card">
        <table className="bop-table">
          <thead>
            <tr>
              <th>Data/ora</th>
              <th>Template</th>
              <th>Modulo</th>
              <th>Utente</th>
              <th>Azione</th>
              <th>Entità</th>
              <th>Destinatario</th>
              <th>Esito</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8}><div className="bop-empty-state">Caricamento...</div></td></tr>
            ) : logs.length === 0 ? (
              <tr><td colSpan={8}><div className="bop-empty-state">Nessun log trovato</div></td></tr>
            ) : (
              logs.map(l => (
                <tr key={l.id} className={l.esito === 'ERRORE' ? 'bop-tr--error' : ''}>
                  <td className="bop-text-muted">{new Date(l.createdAt).toLocaleString('it-IT')}</td>
                  <td>{l.template.nome}</td>
                  <td><span className="bop-badge bop-badge-neutral">{l.template.moduloGestionale}</span></td>
                  <td>
                    {l.utente.nome || l.utente.cognome
                      ? `${l.utente.nome || ''} ${l.utente.cognome || ''}`.trim()
                      : l.utente.email}
                  </td>
                  <td><span className={`bop-badge ${BADGE_AZIONE[l.tipoAzione] || 'bop-badge-neutral'}`}>{l.tipoAzione}</span></td>
                  <td>{l.entitaType} #{l.entitaId}</td>
                  <td>{l.tipoAzione === 'EMAIL' && l.destinatarioEmail ? l.destinatarioEmail : <span className="bop-text-muted">—</span>}</td>
                  <td>
                    <span className={`bop-badge ${BADGE_ESITO[l.esito] || 'bop-badge-neutral'}`}>{l.esito}</span>
                    {l.esito === 'ERRORE' && l.erroreDettaglio && (
                      <div className="bop-text-danger">{l.erroreDettaglio}</div>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <ApiInspector docs={API_DOCS} />
    </Layout>
  );
}
